import { useCallback, useMemo, useState } from "react";
import { useToast } from "../../../Hooks/ToasContext";
import { ISellerEntity } from "../../../Services/api/sellers/sellers.interface";
import { ListSellersUseCase } from "../../../Usecase/Sellers/list-sellers-usecase";
import { IListSearchPayload } from "../../../Components/List/list.types";
import { DestroySellersUseCase } from "../../../Usecase/Sellers/destroy-sellers-usecase";

interface IuseListSellersProps {
    listSellersUseCase: ListSellersUseCase
    destroySellersUseCase: DestroySellersUseCase
}

export function useListSellers({ listSellersUseCase, destroySellersUseCase }: IuseListSellersProps){
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState(false)
    const [data, setData] = useState<ISellerEntity[]>([])
    const [totalRegisters, setTotalRegisters] = useState(0)
    const [sellerToResetPassword, setSellerToResetPassword] = useState<ISellerEntity | null>(null)
    const [sellerToDestroy, setSellerToDestroy] = useState('')
    const [currentSearch, setCurrentSearch] = useState('')
    const { showToast } =  useToast()

    const listSellers = useCallback(async ({ page: currentPage, search = '' }: { page : number; search?: string }) => {
        try {
          setLoading(true)
          setLoadError(false)
          
          const listResult = await listSellersUseCase.handle({ page: currentPage, searchBy: search });

          if (listResult.isFailure) {
            setLoadError(true)
            showToast(String(listResult?.error?.error), 'error');
            return;
          }

         const list = listResult.getValue()

         if(list){
            setData(list.data)
            setTotalRegisters(list.total)
            setLoadError(false)
         }

         setCurrentSearch(search)
        } catch (error) {
          setLoadError(true)
          console.error("error useListSellers function: ", error)
        } finally {
          setLoading(false);
        }
      }, [listSellersUseCase, showToast]);

    const handleSearch = useCallback(({ page, searchBy }: IListSearchPayload) => {
      listSellers({ page, search: searchBy })
    }, [listSellers]);

    const handlePageChange = useCallback(({ page, searchBy }: IListSearchPayload) => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      listSellers({ page, search: searchBy ?? currentSearch })
    }, [listSellers, currentSearch]);

    const handleDestroySeller = useCallback(async () => {
      try {
        setLoading(true)

        const destroyResult = await destroySellersUseCase.handle(sellerToDestroy)

        if (destroyResult.isFailure) {
          showToast(String(destroyResult?.error?.error || 'Não foi possível remover o vendedor.'), 'error')
          return
        }

        showToast('Vendedor removido com sucesso.', 'success')
        setSellerToDestroy('')
        listSellers({ page: 1, search: currentSearch })
      } catch (error) {
        console.error("error handleDestroySeller function: ", error)
        showToast('Não foi possível remover o vendedor.', 'error')
      } finally {
        setLoading(false)
      }
    }, [currentSearch, destroySellersUseCase, listSellers, sellerToDestroy, showToast])

    const list = useMemo(() => {
        return data?.map(seller => ({ ...seller })) || []
    }, [data])

    const sellerToDestroyName = useMemo(() => {
      if (!sellerToDestroy) return ''
      return data.find((seller) => seller.id === sellerToDestroy)?.name || 'este vendedor'
    }, [data, sellerToDestroy])

    return {
        loading,
        loadError,
        listSellers,
        list,
        data,
        sellerToResetPassword,
        setSellerToResetPassword,
        sellerToDestroy,
        sellerToDestroyName,
        setSellerToDestroy,
        handleDestroySeller,
        totalRegisters,
        handleSearch,
        handlePageChange,
        currentSearch,
    }
}
